/**
 * Utility functions for managing cache keys across stores
 */

/**
 * Generate a universal cache key from any number of parameters
 * @param {...any} args - Any parameters that affect the data request
 * @returns {string} - A cache key string
 */
export function generateCacheKey(...args) {
    const key = args
        .map(arg => {
            // Handle different types of arguments
            if (arg === null || arg === undefined) {
                return "null";
            } else if (Array.isArray(arg)) {
                // For arrays (like symbol lists), sort and join
                return arg
                    .map(item => item?.toString().trim().toUpperCase())
                    .sort()
                    .join(",");
            } else if (typeof arg === "string") {
                // Handle strings (like symbols) - trim and uppercase
                return arg.trim().toUpperCase();
            } else if (typeof arg === "object") {
                // For objects, use JSON.stringify with sorted keys
                try {
                    const ordered = {};
                    Object.keys(arg)
                        .sort()
                        .forEach(key => {
                            // Convert string values to uppercase for consistent keys
                            const value = arg[key];
                            ordered[key] = typeof value === "string" ? value.toUpperCase() : value;
                        });
                    return JSON.stringify(ordered);
                } catch {
                    return String(arg);
                }
            } else {
                // For boolean, number, etc.
                return String(arg);
            }
        })
        .join("|");
    return key;
}

/**
 * Universal cache clearing function that can be used across stores
 *
 * @param {Object} cacheState - An object containing cache-related state objects
 * @param {Object} cacheState.dataCache - The data cache object to clear (e.g., state.quotes, state.moversData)
 * @param {Object} cacheState.timeCache - The cache timestamps object to clear (e.g., state.lastFetchTime)
 * @param {Object} options - Options for clearing the cache
 * @param {Object|null} [options.filters=null] - Filter object mapping indices or names to expected values
 * @returns {number} - The number of cache entries cleared
 */
export function clearCache({ dataCache, timeCache }, { filters = null } = {}) {
    // If no filters are provided, clear all cache
    if (!filters) {
        const count = Object.keys(dataCache).length;
        Object.keys(dataCache).forEach(key => {
            delete dataCache[key];
            if (timeCache && timeCache[key]) delete timeCache[key];
        });
        return count;
    }

    // Clear only matching cache entries
    let clearedCount = 0;
    Object.keys(dataCache).forEach(key => {
        const keyParts = key.split("|");

        // Check if this key matches all the filter criteria
        const matches = Object.entries(filters).every(([index, filterValue]) => {
            // Skip null/undefined filters (they match anything)
            if (filterValue === null || filterValue === undefined) {
                return true;
            }

            const keyIndex = parseInt(index);
            // Handle numeric indices or named parameters
            const keyPart = !isNaN(keyIndex) ? keyParts[keyIndex] : null;

            // If the filter is for a special values like "all"
            if (filterValue === "all" && (keyPart === "all" || keyPart === "ALL")) {
                return true;
            }

            // Handle array of values
            if (Array.isArray(filterValue)) {
                const normalizedFilter = filterValue.map(v =>
                    typeof v === "string" ? v.trim().toUpperCase() : String(v)
                );
                return normalizedFilter.includes(
                    typeof keyPart === "string" ? keyPart.trim().toUpperCase() : keyPart
                );
            }

            // Handle string values (case insensitive)
            if (typeof filterValue === "string" && typeof keyPart === "string") {
                return filterValue.trim().toUpperCase() === keyPart.trim().toUpperCase();
            }

            // Handle other value types
            return String(filterValue) === keyPart;
        });

        if (matches) {
            delete dataCache[key];
            if (timeCache && timeCache[key]) delete timeCache[key];
            clearedCount++;
        }
    });

    return clearedCount;
}
